import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	companyConfigRestCrudThunks,
	companyConfigRestCrudThunksForSupplier,
} from '../../thunks/company_config_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { Content } from 'antd/lib/layout/layout';
import { Card, Checkbox, Col, Row, Spin, Table, Tooltip, message } from 'antd';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { LoadingOutlined } from '@ant-design/icons';
import {
	PERMISSION_NAMES,
	getSupplierAccessDefaultConfig,
	isInternalTech,
} from '../../utils/AuthUtils';
import posthog from 'posthog-js';
import { REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG } from '../../types/entity_add_requests';

const getFieldId = (field) => `${field.groupKey}-${field.permissionKey}-${field.roleKey}`;

const ROLE_LABLES = {
	managersAllowed: 'Manager',
	techsAllowed: 'Field Tech',
	usersAllowed: 'User',
	inventoryManagerAllowed: 'Inventory Manager',
	accountingManagerAllowed: 'Accounting Manager',
};

// Permissions whose Field Tech column is locked because field-tech stock-location access is
// managed per-tech on the field tech detail page, not via these role-level toggles.
const FIELD_TECH_DISABLED_PERMISSIONS = [PERMISSION_NAMES.MANAGE_STOCK_LOCATIONS];
const FIELD_TECH_DISABLED_TOOLTIP =
	'Field Tech stock locations are managed in the field tech detail page';

const SupplierAdminAccessControlPage: FC<any> = ({
	currentUser,
	companySettings,
	updateCompanySettings,
	getCompanySettings,
	companySettingsFetching,
}): React.ReactElement => {
	const [currentUpdatingKey, setCurrentUpdatingKey] = useState(null);

	useEffect(() => {
		getCompanySettings(currentUser);
	}, [currentUser, getCompanySettings]);

	const isInternalServiceTeam = useMemo(() => isInternalTech(currentUser), [currentUser]);

	const permissionsChanged = useCallback(
		(key) => (e) => {
			if (currentUpdatingKey) {
				return;
			}

			const updatedSetting = {
				...companySettings,
				config: {
					...companySettings.config,
					accessPermissions: {
						...nullSafeGetOrElse('config.accessPermissions', companySettings, {}),
						[key.groupKey]: {
							...nullSafeGetOrElse(`config.accessPermissions.${key.groupKey}`, companySettings, {}),
							[key.permissionKey]: {
								...nullSafeGetOrElse(
									`config.accessPermissions.${key.groupKey}.${key.permissionKey}`,
									companySettings,
									getSupplierAccessDefaultConfig(key.permissionKey)
								),
								[key.roleKey]: e.target.checked,
							},
						},
					},
				},
			};

			setCurrentUpdatingKey(getFieldId(key));

			updateCompanySettings(updatedSetting)
				.catch(() => message.error('Unable to update Access Control!'))
				.finally(() => setCurrentUpdatingKey(null));
		},
		[companySettings, currentUpdatingKey, updateCompanySettings]
	);

	// Returns whether a role's checkbox is disabled plus the tooltip reason. "Request New Service
	// Providers" depends on the same role's service-provider-page access and is redundant once the
	// role can add/remove service providers outright — same rules the buyer page applies.
	const getDisabledInfo = useCallback(
		(roleKey, permissionKey) => {
			if (roleKey === 'techsAllowed' && FIELD_TECH_DISABLED_PERMISSIONS.includes(permissionKey)) {
				return { disabled: true, reason: FIELD_TECH_DISABLED_TOOLTIP };
			}
			if (permissionKey === PERMISSION_NAMES.REQUEST_NEW_SUPPLIERS) {
				const othersConfig = nullSafeGetOrElse(
					'config.accessPermissions.others',
					companySettings,
					{}
				);
				const accessPageConfig =
					othersConfig[PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE] ||
					getSupplierAccessDefaultConfig(PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE);
				const modifySuppliersConfig =
					othersConfig[PERMISSION_NAMES.MODIFY_SUPPLIERS] ||
					getSupplierAccessDefaultConfig(PERMISSION_NAMES.MODIFY_SUPPLIERS);
				if (!accessPageConfig[roleKey]) {
					return {
						disabled: true,
						reason:
							'This role needs “Access to Service Provider Page” before it can request new service providers.',
					};
				}
				if (modifySuppliersConfig[roleKey]) {
					return {
						disabled: true,
						reason:
							'This role can already add or remove service providers, so requesting new service providers isn’t needed.',
					};
				}
			}
			return { disabled: false, reason: undefined };
		},
		[companySettings]
	);

	const getCheckBoxField = useCallback(
		(roleKey) => ({
			title: ROLE_LABLES[roleKey],
			dataIndex: roleKey,
			render: (cellChecked, record) => {
				const { disabled, reason } = getDisabledInfo(roleKey, record.permissionKey);
				const checkbox = (
					<Checkbox
						checked={cellChecked}
						disabled={disabled}
						onChange={permissionsChanged({ ...record, roleKey })}
					/>
				);
				return (
					<div className="flex flex-row">
						{disabled && reason ? (
							<Tooltip title={reason}>
								<span className="inline-block">{checkbox}</span>
							</Tooltip>
						) : (
							checkbox
						)}
						{currentUpdatingKey === getFieldId({ ...record, roleKey }) && (
							<div className="ml-2">
								<Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
							</div>
						)}
					</div>
				);
			},
		}),
		[currentUpdatingKey, getDisabledInfo, permissionsChanged]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Permissions',
				dataIndex: 'label',
				width: '25%',
			},
			getCheckBoxField('techsAllowed'),
			getCheckBoxField('usersAllowed'),
			getCheckBoxField('managersAllowed'),
			getCheckBoxField('inventoryManagerAllowed'),
			getCheckBoxField('accountingManagerAllowed'),
		],
		[getCheckBoxField]
	);

	const getExistingValues = useCallback(
		(groupName, permissionName) =>
			nullSafeGetOrElse(
				`config.accessPermissions.${groupName}.${permissionName}`,
				companySettings,
				getSupplierAccessDefaultConfig(permissionName)
			),
		[companySettings]
	);

	const requestToAddEnabled = !!posthog.isFeatureEnabled(REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG);

	const permissionGroupings = useMemo(
		() => [
			{
				key: 'locations',
				label: 'Locations',
				values: [
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.MODIFY_LOCATIONS,
									label: 'Modify Locations',
								},
								{
									key: PERMISSION_NAMES.MODIFY_LOCATION_TYPES,
									label: 'Modify Location Types',
								},
								{
									key: PERMISSION_NAMES.MODIFY_AREAS,
									label: 'Modify Areas',
								},
								{
									key: PERMISSION_NAMES.SHOW_INTERNAL_TECH_NOTES,
									label: 'Location Staff Notes & Attachments',
								},
						  ]
						: []),
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS,
									label: 'Access Location Documents',
								},
								{
									key: PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
									label: 'Modify Location Documents',
								},
								{
									key: PERMISSION_NAMES.DELETE_STOCK_LOCATIONS,
									label: 'Delete Stock Locations',
								},
						  ]
						: []),
				],
			},
			{
				key: 'stockLocations',
				label: 'Stock Locations',
				values: [
					{
						key: PERMISSION_NAMES.MANAGE_STOCK_LOCATIONS,
						label: 'Manage Stock Locations',
					},
					{
						key: PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION,
						label: 'Allow Bulk Operations While Managing Stock Location',
					},
				],
			},
			{
				key: 'fieldTechConfigurations',
				label: 'Field Tech Management',
				values: [
					{
						key: PERMISSION_NAMES.ASSIGN_ACCESS_TO_LOCATIONS,
						label: 'Assign Access To Locations',
					},
					{
						key: PERMISSION_NAMES.ASSIGN_ACCESS_TO_STOCK_LOCATIONS,
						label: 'Assign Access To Stock Locations',
					},
					{
						key: PERMISSION_NAMES.ASSIGN_MANAGE_STOCK_LOCATIONS,
						label: 'Assign To Manage Stock Locations',
					},
					{
						key: PERMISSION_NAMES.ASSIGN_WORK_ORDER_ROUTING,
						label: 'Assign Work Order Routing',
					},
					{
						key: PERMISSION_NAMES.ASSIGN_PROBLEM_TYPES,
						label: 'Assign Problem Types',
					},
					{
						key: PERMISSION_NAMES.ASSIGN_SUPERVISOR,
						label: 'Assign Supervisor',
					},
					{
						key: PERMISSION_NAMES.MODIFY_FIELD_TECH_DETAILS,
						label: 'Edit Field Tech Details',
					},
				],
			},
			...(isInternalServiceTeam
				? [
						{
							key: 'assets',
							label: 'Assets',
							values: [
								{
									key: PERMISSION_NAMES.MODIFY_ASSETS,
									label: 'Add/Modify Assets',
								},
								{
									key: PERMISSION_NAMES.ENRICH_ASSETS_FROM_INVOICES,
									label: 'Enrich Assets from Invoices',
								},
								{
									key: PERMISSION_NAMES.VIEW_ASSET_LABELS,
									label: 'View Asset Labels',
									bucketKey: 'labels',
								},
								{
									key: PERMISSION_NAMES.MODIFY_ASSET_LABELS,
									label: 'Add/Edit Asset Labels',
									bucketKey: 'labels',
								},
								{
									key: PERMISSION_NAMES.APPLY_ASSET_LABELS,
									label: 'Apply Labels To Assets',
									bucketKey: 'labels',
								},
								{
									key: PERMISSION_NAMES.DELETE_ASSETS,
									label: 'Delete Assets',
								},
								{
									key: PERMISSION_NAMES.MODIFY_ASSET_TYPES,
									label: 'Add/Modify Asset Types',
								},
								{
									key: PERMISSION_NAMES.SHOW_ASSET_PRICE,
									label: 'Show Asset Price',
								},
								{
									key: PERMISSION_NAMES.SHOW_ASSET_INTERNAL_ATTACHMENTS,
									label: 'Show Asset Attachments',
								},
								{
									key: PERMISSION_NAMES.MODIFY_ASSET_INTERNAL_ATTACHMENTS,
									label: 'Upload Asset Attachments',
								},
								{
									key: PERMISSION_NAMES.CREATE_ASSET_CYCLE_EVENTS,
									label: 'Create Asset Cycle Count Events',
								},
								{
									key: PERMISSION_NAMES.EXECUTE_ASSET_CYCLE_RUNS,
									label: 'Execute Asset Cycle Count Runs',
								},
								{
									key: PERMISSION_NAMES.VIEW_REPLACEMENT_INDEX,
									label: 'View Replacement Index',
									bucketKey: 'replacementIndex',
								},
								{
									key: PERMISSION_NAMES.UPDATE_REPLACEMENT_INDEX,
									label: 'Update Replacement Index',
									bucketKey: 'replacementIndex',
								},
								{
									key: PERMISSION_NAMES.RUN_DEEP_ANALYSIS,
									label: 'Run Deep Analysis on Replacement Index',
									bucketKey: 'replacementIndex',
								},
							],
						},
				  ]
				: []),
			...(isInternalServiceTeam
				? [
						{
							key: 'approvals',
							label: 'Approvals',
							values: [
								{
									key: PERMISSION_NAMES.CAN_MARK_WORK_ORDER_COMPLETED,
									label: 'Can Mark Work Order Completed',
								},
								{
									key: PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED,
									label: 'Can Review Work Order Completion',
								},
								{
									key: PERMISSION_NAMES.CAN_APPROVE_WORK_ORDERS,
									label: 'Can Approve/Defer Work Orders',
								},
								{
									key: PERMISSION_NAMES.CAN_APPROVE_INVOICES,
									label: 'Can Approve Invoices',
								},
								{
									key: PERMISSION_NAMES.CAN_APPROVE_QUOTES,
									label: 'Can Approve Quotes',
								},
								{
									key: PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID,
									label: 'Can Mark Invoices Processing/Paid',
								},
							],
						},
				  ]
				: []),
			...(isInternalServiceTeam
				? [
						{
							key: 'siteSurveys',
							label: 'Site Surveys',
							values: [
								{
									key: PERMISSION_NAMES.MODIFY_SITE_SURVEYS,
									label: 'Add/Modify Site Surveys',
								},
								{
									key: PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES,
									label: 'Add/Modify Site Survey Templates',
								},
								{
									key: PERMISSION_NAMES.ADD_WALKTHROUGHS,
									label: 'Add Walkthroughs',
								},
								{
									key: PERMISSION_NAMES.DELETE_WALKTHROUGHS,
									label: 'Delete Walkthroughs',
								},
							],
						},
				  ]
				: []),
			...(isInternalServiceTeam
				? [
						{
							key: 'workOrders',
							label: 'Work Orders',
							values: [
								{
									key: PERMISSION_NAMES.REMOTE_CHECK_IN_OR_CHECK_OUT_ALLOWED,
									label: 'Allow Remote Check In/Check Out',
								},
							],
						},
				  ]
				: []),
			...(isInternalServiceTeam
				? [
						{
							key: 'others',
							label: 'Service Providers',
							values: [
								{
									key: PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE,
									label: 'Access to Service Provider Page',
								},
								...(requestToAddEnabled
									? [
											{
												key: PERMISSION_NAMES.REQUEST_NEW_SUPPLIERS,
												label: 'Request New Service Providers',
											},
									  ]
									: []),
								{
									key: PERMISSION_NAMES.MODIFY_SUPPLIERS,
									label: 'Add Or Remove Service Providers',
								},
								{
									key: PERMISSION_NAMES.EDIT_SUPPLIER_NETWORK_ASSOCIATION,
									label: 'Edit Service Provider Network Associations',
								},
								{
									key: PERMISSION_NAMES.RANK_SUPPLIERS,
									label: 'Modify Service Provider Rankings',
								},
								{
									key: PERMISSION_NAMES.EDIT_SUPPLIER_CONTACT,
									label: 'Edit Service Provider Contact',
								},
								{
									key: PERMISSION_NAMES.SHOW_SUPPLIER_INTERNAL_ATTACHMENTS,
									label: 'Show Service Provider Internal Attachments',
								},
								{
									key: PERMISSION_NAMES.MODIFY_SUPPLIER_INTERNAL_ATTACHMENTS,
									label: 'Upload Service Provider Internal Attachments',
								},
							],
						},
				  ]
				: []),
			...(isInternalServiceTeam
				? [
						{
							key: 'workflows',
							label: 'Workflows',
							values: [
								{
									key: PERMISSION_NAMES.VIEW_TROUBLESHOOTING,
									label: 'View Troubleshooting',
								},
								{
									key: PERMISSION_NAMES.ADD_TROUBLESHSHOOTING,
									label: 'Add Troubleshooting',
								},
								{
									key: PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING,
									label: 'Modify Troubleshooting',
								},
								{
									key: PERMISSION_NAMES.DELETE_TROUBLESHSHOOTING,
									label: 'Delete Troubleshooting',
								},
								{
									key: PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS,
									label: 'Modify Troubleshooting Associations',
								},
								{
									key: PERMISSION_NAMES.VIEW_BUDGET,
									label: 'View Budget',
								},
								{
									key: PERMISSION_NAMES.ADD_BUDGET,
									label: 'Add Budget',
								},
								{
									key: PERMISSION_NAMES.MODIFY_BUDGET,
									label: 'Modify Budget',
								},
								{
									key: PERMISSION_NAMES.DELETE_BUDGET,
									label: 'Delete Budget',
								},
							],
						},
				  ]
				: []),
			{
				key: 'others',
				label: 'Others',
				values: [
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.ACCESS_ANALYTICS,
									label: 'Access Analytics',
								},
						  ]
						: []),
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.MODIFY_PM,
									label: 'Modify Planned Maintenance',
								},
						  ]
						: []),
					{
						key: PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS,
						label: 'Modify Parts and Equipment',
					},
					{
						key: PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_FOR_PARTS,
						label: 'Allow Bulk Operations for Parts',
					},
					{
						key: PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_FOR_EQUIPMENT,
						label: 'Allow Bulk Operations for Equipment',
					},
					{
						key: PERMISSION_NAMES.MODIFY_VENDORS,
						label: 'Modify Vendors',
					},
					{
						key: PERMISSION_NAMES.ACCESS_PR_AND_PO,
						label: 'Access Purchase Requests And Orders',
					},
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.ACCESS_DEPRECIATION,
									label: 'Access Depreciation Module',
								},
						  ]
						: []),
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.CAN_BACKDATE_WORK_ORDER,
									label: 'Can Change Reported At For Work Orders',
								},
						  ]
						: []),
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER,
									label: 'Can Upload Invoices For Service Provider',
								},
						  ]
						: []),
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER,
									label: 'Can Upload Quotes For Service Provider',
								},
						  ]
						: []),
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.REOPEN_WORK_ORDERS,
									label: 'Can Re-open Work Orders',
								},
						  ]
						: []),
					{
						key: PERMISSION_NAMES.MODIFY_WO_TASKS,
						label: 'Add/Remove Work Order Tasks',
					},
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.CAN_VIEW_PART_EQUIPMENT_COST_IN_WO,
									label: 'Can View Part/Equipment Cost In Work Orders',
								},
						  ]
						: []),
					{
						key: PERMISSION_NAMES.EDIT_WORK_ORDERS,
						label: 'Can Edit Work Orders',
					},
					{
						key: PERMISSION_NAMES.CANCEL_WORK_ORDERS,
						label: 'Can Cancel Work Orders',
					},
					{
						key: PERMISSION_NAMES.REVISE_SERVICE_CALLS,
						label: 'Can Revise Service Calls',
					},
					...(isInternalServiceTeam
						? [
								{
									key: PERMISSION_NAMES.CAN_VIEW_QUOTES,
									label: 'Can View Quotes',
								},
								{
									key: PERMISSION_NAMES.CAN_VIEW_INVOICES,
									label: 'Can View Invoices',
								},
								{
									key: PERMISSION_NAMES.VIEW_LINKED_WORK_ORDERS,
									label: 'View Linked Work Orders',
								},
								{
									key: PERMISSION_NAMES.MODIFY_LINKING_WORK_ORDERS,
									label: 'Link/Unlink Work Orders',
								},
						  ]
						: []),
					{
						key: PERMISSION_NAMES.VIEW_WORK_ORDER_LABELS,
						label: 'View Work Order Labels',
						bucketKey: 'labels',
					},
					{
						key: PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS,
						label: 'Add/Edit Work Order Labels',
						bucketKey: 'labels',
					},
					{
						key: PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS,
						label: 'Apply Labels To Work Orders',
						bucketKey: 'labels',
					},
				],
			},
		],
		[isInternalServiceTeam, requestToAddEnabled]
	);

	const dataSource = useMemo(
		() =>
			permissionGroupings
				// A group's rows depend on role (e.g. Locations has only internal-service-team rows for
				// non-internal admins). Drop any group left with no rows so we never render an empty card.
				.filter((group) => group.values.length > 0)
				.map((group) => ({
					...group,
					fields: group.values.map((permission) => {
						const groupKey = (permission as any).bucketKey || group.key;
						return {
							...permission,
							groupKey,
							permissionKey: permission.key,
							...getExistingValues(groupKey, permission.key),
						};
					}),
				})),
		[getExistingValues, permissionGroupings]
	);

	return (
		<Content style={{ padding: '0 0.5em' }}>
			<Row className="rowSpacing">
				{dataSource.map((group) => (
					<Col span={24} key={group.key} className="mb-2">
						<Card title={group.label} loading={companySettingsFetching}>
							<Table columns={columns} dataSource={group.fields} showHeader pagination={false} />
						</Card>
					</Col>
				))}
			</Row>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,

	companySettings: state.company_config.detail,
	companySettingsFetching: state.company_config.fetching,
});

const mapDispatchToProps = (dispatch) => ({
	getCompanySettings: (contact) =>
		dispatch(companyConfigRestCrudThunks(ROLE_TYPES.SUPPLIER, contact)),
	updateCompanySettings: (settings) =>
		dispatch(companyConfigRestCrudThunksForSupplier.update(settings)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SupplierAdminAccessControlPage)
);
